import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import api from "../../lib/api";
import { errorMessage } from "../../lib/stays";
import { useAuth } from "../../context/AuthContext";

export default function GuestsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => { setSearch(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { page, page_size: 15, ...(search ? { q: search } : {}), ...(role ? { role } : {}) } });
      setRows(data.results); setMeta(data);
    } catch (e) { toast.error(errorMessage(e, "Couldn't load users")); setRows([]); }
  }, [page, search, role]);
  useEffect(() => { load(); }, [load]);

  const patch = async (u, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await api.patch(`/admin/users/${u.id}`, body); toast.success("Updated"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const sel = "bg-[#0f0f0f] border border-white/10 h-10 px-2 text-sm text-white";

  return (
    <div data-testid="admin-guests">
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" aria-label="Search users" className="pl-9 bg-[#0f0f0f] border-white/10 h-10" /></div>
        <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} className={sel} aria-label="Filter by role"><option value="">Everyone</option><option value="guest">Guests</option><option value="admin">Admins</option></select>
      </div>

      <div className="space-y-3" aria-busy={rows === null}>
        {rows === null ? [...Array(4)].map((_, i) => <div key={i} className="glass h-16 animate-pulse" />)
          : rows.length === 0 ? <div className="text-center text-white/40 py-12">No users found</div>
            : rows.map((u) => {
              const me = u.id === user?.id;
              return (
                <div key={u.id} className={`glass p-4 grid sm:grid-cols-[1.4fr_1fr_90px_auto] gap-3 items-center ${u.is_active ? "" : "opacity-50"}`} data-testid={`user-${u.id}`}>
                  <div className="min-w-0"><div className="text-white truncate">{u.name} {me && <span className="text-[10px] text-gold border border-gold/30 px-1.5 ml-1">you</span>}</div><div className="text-xs text-white/40 truncate">{u.email}{u.phone && ` · ${u.phone}`}</div></div>
                  <div className="text-xs text-white/40">Joined {new Date(u.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}<br />{u.bookings_count} booking{u.bookings_count !== 1 ? "s" : ""}</div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-1 text-center ${u.role === "admin" ? "border-gold/40 text-gold" : "border-white/15 text-white/50"}`}>{u.role}</span>
                  <div className="flex gap-2 text-xs justify-end">
                    <button disabled={me} onClick={() => patch(u, { is_active: !u.is_active }, u.is_active ? `Deactivate ${u.email}? They won't be able to sign in.` : null)} className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-30">{u.is_active ? "Deactivate" : "Activate"}</button>
                    <button disabled={me} onClick={() => patch(u, { role: u.role === "admin" ? "guest" : "admin" }, u.role === "admin" ? `Remove admin access for ${u.email}?` : `Give ${u.email} full admin access?`)} className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-30">{u.role === "admin" ? "Make guest" : "Make admin"}</button>
                  </div>
                </div>
              );
            })}
      </div>

      <div className="flex items-center justify-between mt-6 text-sm text-white/50">
        <span>{meta.total} user{meta.total !== 1 ? "s" : ""}</span>
        <div className="flex items-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-30" aria-label="Previous page"><ChevronLeft size={18} /></button>
          <span>Page {meta.page} of {meta.pages}</span>
          <button disabled={page >= meta.pages} onClick={() => setPage(page + 1)} className="disabled:opacity-30" aria-label="Next page"><ChevronRight size={18} /></button>
        </div>
      </div>
    </div>
  );
}
